/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Code Indexing Operations
 * 
 * Handles codebase indexing for semantic code search.
 */

import { authenticatedFetch } from '@shared/lib/api';
import { handleApiResponse } from '@shared/lib/apiResponse';

/**
 * Index a codebase for semantic code search
 * SkillShop server endpoint - uses authenticatedFetch
 */
export async function indexCodebase(path: string, force: boolean = false): Promise<{
  success: boolean;
  message?: string;
  indexed_at?: string;
  error?: string;
}> {
  try {
    console.log('[SkillShop Service] Indexing codebase:', path);

    const response = await authenticatedFetch('/api/code-index/index', {
      method: 'POST',
      body: {
        path,
        force,
        splitter: 'ast'
      }
    });

    const result = await handleApiResponse<{
      path: string;
      success: boolean;
      message: string;
      indexed_at: string;
    }>(response);

    console.log('[SkillShop Service] Codebase indexing completed:', result);
    return {
      success: true,
      message: result.message,
      indexed_at: result.indexed_at
    };
  } catch (error: any) {
    console.error('[SkillShop Service] Error indexing codebase:', error);
    return {
      success: false,
      error: error.message || 'Failed to index codebase'
    };
  }
}

/**
 * Get the indexing status of a codebase
 * SkillShop server endpoint - uses authenticatedFetch
 * 
 * This endpoint returns detailed status information including progress percentage
 * when indexing is in progress, read from the claude-context snapshot file.
 */
export async function getCodeIndexStatus(path: string): Promise<{
  status: 'indexed' | 'not_indexed' | 'indexing' | 'error' | 'unavailable' | 'unknown';
  mcp_connected: boolean;
  progress?: number;
  file_count?: number;
  chunk_count?: number;
  index_status?: 'completed' | 'limit_reached';
  last_indexed?: string;
  last_updated?: string;
  last_attempted_progress?: number;
  message?: string;
  checked_at?: string;
  error?: string;
}> {
  try {
    console.log('[SkillShop Service] Getting code index status:', path);

    const response = await authenticatedFetch(`/api/code-index/status?path=${encodeURIComponent(path)}`);

    const result = await handleApiResponse<{
      path: string;
      status: string;
      mcp_connected: boolean;
      progress?: number;
      file_count?: number;
      chunk_count?: number;
      index_status?: 'completed' | 'limit_reached';
      last_indexed?: string;
      last_updated?: string;
      last_attempted_progress?: number;
      message?: string;
      checked_at?: string;
    }>(response);

    console.log('[SkillShop Service] Code index status:', result);
    return {
      status: result.status as any,
      mcp_connected: result.mcp_connected,
      progress: result.progress,
      file_count: result.file_count,
      chunk_count: result.chunk_count,
      index_status: result.index_status,
      last_indexed: result.last_indexed,
      last_updated: result.last_updated,
      last_attempted_progress: result.last_attempted_progress,
      message: result.message,
      checked_at: result.checked_at
    };
  } catch (error: any) {
    console.error('[SkillShop Service] Error getting code index status:', error);
    return {
      status: 'error',
      mcp_connected: false,
      error: error.message || 'Failed to get code index status'
    };
  }
}

/**
 * Clear the code index for a codebase
 * SkillShop server endpoint - uses authenticatedFetch
 */
export async function clearCodeIndex(path: string): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> {
  try {
    console.log('[SkillShop Service] Clearing code index:', path);

    const response = await authenticatedFetch(`/api/code-index/index?path=${encodeURIComponent(path)}`, {
      method: 'DELETE'
    });

    const result = await handleApiResponse<{
      path: string;
      success: boolean;
      message: string;
    }>(response);

    console.log('[SkillShop Service] Code index cleared:', result);
    return {
      success: true,
      message: result.message
    };
  } catch (error: any) {
    console.error('[SkillShop Service] Error clearing code index:', error);
    return {
      success: false,
      error: error.message || 'Failed to clear code index'
    };
  }
}

/**
 * Get MCP server connection status
 * SkillShop server endpoint - uses authenticatedFetch
 */
export async function getMcpStatus(): Promise<{
  connected: boolean;
  tools: string[];
  last_error?: string;
}> {
  try {
    const response = await authenticatedFetch('/api/code-index/mcp-status');

    const result = await handleApiResponse<{
      connected: boolean;
      tools: string[];
      last_error?: string;
    }>(response);

    return result;
  } catch (error: any) {
    console.error('[SkillShop Service] Error getting MCP status:', error);
    return {
      connected: false,
      tools: []
    };
  }
}

/**
 * Response type for getCodeIndexStatusByProduct
 */
export interface ProductCodeIndexStatus {
  productId: string;
  path?: string;
  status: 'indexed' | 'not_indexed' | 'indexing' | 'error' | 'unavailable' | 'unknown' | 'not_configured';
  mcp_connected: boolean;
  progress?: number;
  file_count?: number;
  chunk_count?: number;
  index_status?: 'completed' | 'limit_reached';
  last_indexed?: string;
  last_updated?: string;
  last_attempted_progress?: number;
  message?: string;
  checked_at?: string;
  error?: string;
}

/**
 * Get the indexing status for a product by its ID
 * 
 * This is the preferred method for getting code index status as it handles
 * all path resolution server-side based on the product's repository configuration.
 * 
 * @param productId - UUID of the product to check
 */
export async function getCodeIndexStatusByProduct(productId: string): Promise<ProductCodeIndexStatus> {
  try {
    console.log('[SkillShop Service] Getting code index status for product:', productId);

    const response = await authenticatedFetch(`/api/code-index/product/${encodeURIComponent(productId)}/status`);

    const result = await handleApiResponse<{
      productId: string;
      path?: string;
      status: string;
      mcp_connected: boolean;
      progress?: number;
      file_count?: number;
      chunk_count?: number;
      index_status?: 'completed' | 'limit_reached';
      last_indexed?: string;
      last_updated?: string;
      last_attempted_progress?: number;
      message?: string;
      checked_at?: string;
    }>(response);

    console.log('[SkillShop Service] Product code index status:', result);
    return {
      productId: result.productId,
      path: result.path,
      status: result.status as ProductCodeIndexStatus['status'],
      mcp_connected: result.mcp_connected,
      progress: result.progress,
      file_count: result.file_count,
      chunk_count: result.chunk_count,
      index_status: result.index_status,
      last_indexed: result.last_indexed,
      last_updated: result.last_updated,
      last_attempted_progress: result.last_attempted_progress,
      message: result.message,
      checked_at: result.checked_at
    };
  } catch (error: any) {
    console.error('[SkillShop Service] Error getting code index status for product:', error);
    return {
      productId,
      status: 'error',
      mcp_connected: false,
      error: error.message || 'Failed to get code index status for product'
    };
  }
}

